import { AppConstants } from '@unpispas/upp-defs';
import { CountryCodes } from '@unpispas/upp-defs';
import { TaxIdValidators } from '@unpispas/upp-defs';
import { configService } from '@unpispas/upp-base';
import { dataService } from '../../data';

import { DataObject, ObjectOptions, RelatedObject } from "../base";
import { CreateObject } from "../item";

import { TicketChange } from './ticketchange';
import { AccountInvoice } from './accountinvoice';

import { Ticket } from './ticket';
import { TicketData } from './ticketdata';

import type { TicketDataRegistry } from './ticketdata';
import type { TicketView } from '../views/ticket';

/****************************/
/* SCHEMA DEFINITION        */
/****************************/

import { TicketChangeType } from '../types';
import { AccountInvoiceType } from '../types';

const _schema = {
    "name": "VERIFACTU",
    "fields": [
        { name: "tckchg", alias: "change", type: "objid" },
        { name: "invceac", alias: "invoice", type: "objid" },
        { name: "qr", type: "string" },
        { name: "xml", type: "string" },
        { name: "sign", type: "string" },
        { name: "error", type: "string" },
        { name: "sent", type: "date" },
        { name: "valid", type: "string" }
    ],
    relate: [
        { direction: ">", target: "TICKETCHANGE", by: "change", name: "change", reason: "change", child: false, class: TicketChangeType },
        { direction: ">", target: "ACCOUNTINVOICE", by: "invceac", name: "invoice", reason: "invoice", child: false, class: AccountInvoiceType },
    ]
} as const;

const _VerifactuClass = CreateObject(_schema);
if (!_VerifactuClass) {
    throw new Error("Failed to create BaseClass for '" + _schema.name + "'");
}

/****************************/
/* TYPES DECLARATION        */
/****************************/

/** Supported VeriFactu document operations. */
type VerifactuOperation = 'ALTA' | 'BAJA';

/** VAT breakdown line used in VeriFactu payloads. */
type VerifactuTaxDetail = {
    rate: number;
    base: string;
    amount: string;
};

/** Invoice recipient data required for full invoices. */
type VerifactuRecipient = {
    name: string;
    taxId: string;
    country: string | null;
};

/** Alternative recipient identification when NIF is not applicable. */
type VerifactuRecipientIdOtro = {
    codigoPais: string;
    idType: '02' | '03' | '04' | '05' | '06';
    id: string;
};

/** Minimal invoice reference used for rectifications and substitutions. */
type VerifactuInvoiceRef = {
    number: string;
    date: string;
};

/** Rectification metadata attached to VeriFactu invoice payloads. */
type VerifactuRectification = {
    type: 'S' | 'I';
    amount: {
        base: string;
        taxes: string;
    } | null;
};

/** Software metadata required by VeriFactu XML schemas. */
type VerifactuSoftware = {
    developerName: string;
    developerTaxId: string;
    appName: string;
    appId: string;
    appVersion: string;
    installation: string;
};

/** Previous invoice chain entry used for signature chaining. */
type VerifactuPrevious = {
    number: string | null;
    date: string | null;
    signature: string | null;
};

/** Device-local cache shape for the last VeriFactu chain entry. */
type VerifactuChainStore = {
    created: string | null;
    series: string | null;
    invoice: string | null;
    signature: string | null;
};

/** Normalized VeriFactu payload consumed by signature and XML builders. */
type VerifactuPayload = {
    operation: VerifactuOperation;
    reason: string | null;
    issuer: {
        name: string;
        taxId: string;
    };
    representative: {
        name: string;
        taxId: string;
    };
    invoice: {
        number: string;
        date: string;
        type: string;
        description: string;
        taxes: string;
        total: string;
        taxesDetail: VerifactuTaxDetail[];
        recipient: VerifactuRecipient | null;
        rectification: VerifactuRectification | null;
        replaces: VerifactuInvoiceRef[];
        rectifies: VerifactuInvoiceRef[];
    };
    cancelled?: {
        taxId: string;
        number: string;
        date: string;
    };
    previous: {
        number: string;
        date: string;
    } | null;
    previousSignature: string | null;
    generatedAt: string;
    software: VerifactuSoftware;
};

/****************************/
/* TYPE HELPERS             */
/****************************/

/** Returns whether recipient can be represented as NIF in VeriFactu XML. */
function verifactuRecipientUsesNif(recipient: VerifactuRecipient): boolean {
    if (recipient.country && !CountryCodes.Is_ES(recipient.country)){
        return false;
    }

    return TaxIdValidators.ValidateTaxId(recipient.taxId);
}

/** Builds IDOtro payload when recipient cannot be represented as NIF. */
function verifactuRecipientIdOtro(recipient: VerifactuRecipient): VerifactuRecipientIdOtro | null {
    if (verifactuRecipientUsesNif(recipient)){
        return null;
    }

    const _country = recipient.country;
    if (!_country){
        return {
            codigoPais: '',
            idType: '06',
            id: recipient.taxId.substring(0, 20),
        };
    }

    const _idType: VerifactuRecipientIdOtro['idType'] = CountryCodes.Is_EU(_country) ? '02' : '04';
    let _id = recipient.taxId;
    if (_id.length > 20){
        _id = _id.substring(0, 20);
    }

    return {
        codigoPais: _country,
        idType: _idType,
        id: _id,
    };
}

/** Returns the chain link when complete; otherwise warns and omits the previous fingerprint. */
function verifactuResolvedPreviousChain(candidate: VerifactuPrevious | null): VerifactuPrevious | null {
    if (!candidate){
        return null;
    }

    const _missing: string[] = [];
    if ((candidate.number == null) || (candidate.number === '')){
        _missing.push('number');
    }
    if ((candidate.date == null) || (candidate.date === '')){
        _missing.push('date');
    }
    if ((candidate.signature == null) || (candidate.signature === '')){
        _missing.push('signature');
    }

    if (_missing.length > 0){
        console.warn('[VERIFACTU] Previous chain link malformed (' + _missing.join(', ') + ')');
        return null;
    }

    return candidate;
}

type TaxBreakdownRow = {
    rate: number;
    base: number;
    amount: number;
    total?: number;
};

/**
 * Drops VAT rows whose rounded base and amount are both zero when a real row remains.
 * Keeps one row when every row is zero so the breakdown is never empty.
 */
function TaxBreakdown(taxes: TaxBreakdownRow[] | null | undefined): TaxBreakdownRow[] {
    const _source = Array.isArray(taxes) ? taxes : [];
    const _normalized = _source.map((tax) => ({
        rate: Number(tax.rate) || 0,
        base: Number(tax.base) || 0,
        amount: Number(tax.amount) || 0,
        ...(tax.total != null ? { total: Number(tax.total) } : {}),
    }));

    if (_normalized.length === 0){
        return [];
    }

    const _real = _normalized.filter((tax) => {
        const _base = Math.round(tax.base * 100) / 100;
        const _amount = Math.round(tax.amount * 100) / 100;
        return _base !== 0 || _amount !== 0;
    });
    if (_real.length > 0){
        return _real;
    }

    return [_normalized[0]];
}

/****************************/
/* CLASS DECLARATION        */
/****************************/

export class Verifactu extends _VerifactuClass {
    /****************************/
    /* PUBLIC API               */
    /****************************/

    /** Creates or hydrates a VeriFactu record bound to the data service. */
    constructor(objid: string | null, data: dataService, objoptions: ObjectOptions | null = null){
        super(_schema, objid, data, objoptions || {});
    }

    /** Creates a detached copy preserving object graph references. */
    Copy(store: Array<DataObject> = []): Verifactu {
        return this._Copy(store) as Verifactu;
    }

    protected override DoChildLinked(relation: string | null, related: RelatedObject | null): void {
        if (this.CopyOf){
            return;
        }

        if (relation === 'change' && related instanceof TicketChange){
            if (related === this.change){
                related.AddVerifactu(this);
            }
            else {
                related.DelVerifactu(this);
            }
            return;
        }

        // relate.by is the FK column (`invceac`), not the alias (`invoice`).
        if (relation === 'invceac' && related instanceof AccountInvoice){
            if (related === this.invoice){
                related.AddVerifactu(this);
            }
            else {
                related.DelVerifactu(this);
            }
        }
    }

    /**
     * Rebuilds VeriFactu payload from linked change or invoice and stores XML/signature/QR.
     * @returns Whether generation completed successfully.
     */
    async Refresh(): Promise<boolean> {
        if (this.change){
            return this._RefreshFromChange(this.change);
        }

        if (this.invoice){
            return this._RefreshFromInvoice(this.invoice);
        }

        console.error('[VERIFACTU] No ticket change or account invoice provided.');
        return false;
    }

    /** Indicates whether current VeriFactu payload has been validated by backend. */
    get IsValid(): boolean {
        return (this.valid != null);
    }

    /****************************/
    /* PRIVATE: MODEL ACCESS    */
    /****************************/

    /** Resolves ticketdata from the linked ticket change, when available. */
    private get ticketdata(): TicketData | null {
        return this.change?.ticket?.ticketdata || null;
    }

    /****************************/
    /* PRIVATE: REFRESH FLOW    */
    /****************************/

    /** Builds VeriFactu output from a ticket change registry snapshot. */
    private async _RefreshFromChange(change: TicketChange): Promise<boolean> {
        const _registry = change.ticket?.ticketdata?.Registry;
        if (!_registry || !_registry.ticketRef){
            console.error('[VERIFACTU] Invalid or missing ticketdata.registry.');
            return false;
        }

        const _cachedChain = this.ReadCachedChain(change, null);
        const _calc = _VerifactuCalc.Create(_registry, this.data.serial || null, change.ticket, _cachedChain);
        if (!_calc){
            console.error('[VERIFACTU] Registry is not suitable for VeriFactu calculation.');
            return false;
        }

        const _payload = _calc.Payload();
        return this._ApplyPayload(_calc, _payload, change, null);
    }

    /** Builds VeriFactu output from an account invoice and cached chain entry. */
    private async _RefreshFromInvoice(invoice: AccountInvoice): Promise<boolean> {
        if (!invoice.invoice){
            console.error('[VERIFACTU] Account invoice number is missing.');
            return false;
        }

        const _cachedChain = this.ReadCachedChain(null, invoice);
        const _calc = _VerifactuInvoiceCalc.Create(invoice, _cachedChain, this.data.serial || null);
        if (!_calc){
            console.error('[VERIFACTU] Account invoice is not suitable for VeriFactu calculation.');
            return false;
        }

        const _payload = _calc.Payload();
        return this._ApplyPayload(_VerifactuCalc.CreateEmitter(), _payload, null, invoice);
    }

    /** Signs payload, stores generated artifacts, and updates local chain cache. */
    private async _ApplyPayload(calc: _VerifactuCalc, payload: VerifactuPayload | null, change: TicketChange | null, invoice: AccountInvoice | null): Promise<boolean> {
        if (!payload){
            console.error('[VERIFACTU] Could not build VeriFactu payload.');
            return false;
        }

        const _signature = await calc.Signature(payload);
        if (!_signature){
            console.error('[VERIFACTU] Could not calculate VeriFactu signature.');
            return false;
        }

        this.xml = calc.Xml(payload, _signature);
        this.sign = _signature;
        this.qr = calc.Qr(payload);

        if (!this.xml || !this.sign || !this.qr){
            console.error('[VERIFACTU] Could not generate complete VeriFactu output.');
            return false;
        }

        this.StoreCachedChain(change, invoice);

        return true;
    }

    /****************************/
    /* PRIVATE: CACHE HELPERS   */
    /****************************/

    /** Resolves place identifier from linked change or invoice. */
    private _placeid(change: TicketChange | null, invoice: AccountInvoice | null): string {
        if (change){
            return String(change.ticket?.place?.objid ?? null);
        }

        if (invoice){
            return String(invoice.place?.objid ?? null);
        }

        throw new Error('[VERIFACTU] No ticket change or account invoice provided.');
    }

    /** Reads the last cached VeriFactu chain entry for the current place. */
    private ReadCachedChain(change: TicketChange | null, invoice: AccountInvoice | null): VerifactuPrevious | null {
        const _placeid = this._placeid(change, invoice);

        const _cfg = this.data.injector.get(configService);
        const _ticketstore = _cfg.getConfiguration(_placeid + '_DEVICE_TICKETS');
        const _chain = (_ticketstore.get('vftu') ?? null) as VerifactuChainStore | null;
        if (!_chain?.signature){
            return null;
        }

        return {
            number: _chain.series && _chain.invoice
                ? (_chain.series + '/' + _chain.invoice)
                : null,
            date: _chain.created || null,
            // VeriFactu Huella is SHA-256 hex (64); keep full value (no TBAI-style 100 truncate).
            signature: _chain.signature,
        };
    }

    /** Persists the current signature as the next VeriFactu chain entry. */
    private StoreCachedChain(change: TicketChange | null, invoice: AccountInvoice | null): void {
        const _signature = String(this.sign || '').trim();
        if (_signature.length === 0){
            return;
        }

        const _placeid = this._placeid(change, invoice);

        const _cfg = this.data.injector.get(configService);
        const _ticketstore = _cfg.getConfiguration(_placeid + '_DEVICE_TICKETS');
        if (change){
            _ticketstore.set('vftu', {
                created: this._DateOnly(change.created?.toISOString() || null),
                series: change.series || null,
                invoice: change.invoice || null,
                signature: _signature,
            } as VerifactuChainStore);
            return;
        }

        if (invoice){
            _ticketstore.set('vftu', {
                created: this._DateOnly(this._IsoDate(invoice.created)),
                series: invoice.series || null,
                invoice: invoice.invoice || null,
                signature: _signature,
            } as VerifactuChainStore);
        }
    }

    /****************************/
    /* PRIVATE: FORMAT HELPERS  */
    /****************************/

    /** Normalizes date values to ISO-8601 strings. */
    private _IsoDate(value: Date | string | null | undefined): string | null {
        if (!value){
            return null;
        }

        if (value instanceof Date){
            return value.toISOString();
        }

        return String(value);
    }

    /** Formats an ISO date string as DD-MM-YYYY for VeriFactu payloads. */
    private _DateOnly(value: string | null): string | null {
        if (!value){
            return null;
        }

        const _date = new Date(value);
        if (Number.isNaN(_date.getTime())){
            return null;
        }

        const _dd = String(_date.getDate()).padStart(2, '0');
        const _mm = String(_date.getMonth() + 1).padStart(2, '0');
        const _yyyy = _date.getFullYear();
        return _dd + '-' + _mm + '-' + _yyyy;
    }
}

/*******************************/
/* VERIFACTU CHANGE CALC       */
/*******************************/

class _VerifactuCalc {
    /*******************************/
    /* PUBLIC FACTORY              */
    /*******************************/

    /** Validates registry prerequisites and returns a calculation context. */
    static Create(
        registry: TicketDataRegistry | null,
        installationNumber: string | null,
        ticket: Ticket | null = null,
        cachedChain: VerifactuPrevious | null = null
    ): _VerifactuCalc | null {
        if (!registry){
            return null;
        }

        const _current = this.CurrentChange(registry);
        const _merchantTaxId = this.Text(registry.merchant?.taxId);
        const _merchantName = this.Text(registry.merchant?.name);
        if (!_current || !_merchantTaxId || !_merchantName){
            return null;
        }

        const _series = this.Text(_current.series || registry.ticketRef?.series);
        const _invoice = this.Text(_current.invoice || registry.ticketRef?.invceno);
        const _issuedAt = this.Text(_current.date || registry.fiscal?.issuedAt);
        if (!_series || !_invoice || !_issuedAt){
            return null;
        }

        return new _VerifactuCalc(registry, String(installationNumber || 'UNKNOWN'), ticket, cachedChain);
    }

    /** Returns a minimal emitter context for invoice-only VeriFactu generation. */
    static CreateEmitter(): _VerifactuCalc {
        return new _VerifactuCalc({
            merchant: { name: 'Emitter', taxId: '00000000A' },
            changes: [{
                reason: 'F',
                series: 'E',
                invoice: '1',
                date: '2026-01-01T00:00:00.000Z',
                base: 0,
                taxes: 0,
                total: 0,
            }],
            ticketRef: { series: 'E', invceno: '1' },
            commercial: { state: 'ACTIVE', totals: { taxes: [] } },
        } as unknown as TicketDataRegistry, 'UNKNOWN', null, null);
    }

    /*******************************/
    /* PRIVATE: FACTORY HELPERS    */
    /*******************************/

    /** Returns the latest change entry from the registry snapshot. */
    private static CurrentChange(registry: TicketDataRegistry): TicketDataRegistry['changes'][number] | null {
        const _changes = registry.changes || [];
        return (_changes.length > 0) ? _changes[_changes.length - 1] : null;
    }

    /** Returns the previous change entry from the registry snapshot. */
    private static PreviousChange(registry: TicketDataRegistry): TicketDataRegistry['changes'][number] | null {
        const _changes = registry.changes || [];
        return (_changes.length > 1) ? _changes[_changes.length - 2] : null;
    }

    /** Trims and normalizes optional text values. */
    private static Text(value: string | null | undefined): string | null {
        const _text = String(value || '').trim();
        return _text ? _text : null;
    }

    /**
     * Resolves RegistroAnterior from the device/SIF chain cache only.
     * Registry changes[n-2] is for rectification refs, not the VeriFactu hash chain.
     */
    private _ResolvedPreviousChain(): VerifactuPrevious | null {
        const _chain = this.cachedChain;
        if (!_chain?.signature){
            return null;
        }

        const _current = _VerifactuCalc.CurrentChange(this.registry);
        const _currentNumber = (_current?.series || _current?.invoice)
            ? String(_current?.series || '') + '/' + String(_current?.invoice || '')
            : null;
        if (_chain.number && _currentNumber && (_chain.number === _currentNumber)){
            return null;
        }

        return verifactuResolvedPreviousChain({
            number: _chain.number,
            date: _chain.date ? this.DateOnly(_chain.date) : null,
            signature: _VerifactuCalc.Text(_chain.signature),
        });
    }

    /*******************************/
    /* CONSTRUCTOR                 */
    /*******************************/

    /** Private constructor; use Create() or CreateEmitter() to instantiate. */
    private constructor(
        private registry: TicketDataRegistry,
        private installationNumber: string,
        private ticket: Ticket | null = null,
        private cachedChain: VerifactuPrevious | null = null
    ){
        // Keep constructor private to force validation through Create().
    }

    /** Economic 0% on this ticket may be emitted only for an eligible recipient NIF. */
    private _BlocksZeroRate(taxId: string | null | undefined, country: string | null | undefined): boolean {
        const _view = this.ticket?.View as TicketView | null;
        if (!_view?.ZeroRateInvoice){
            return false;
        }

        return !TaxIdValidators.IsZeroRate(taxId, country);
    }

    /*******************************/
    /* PUBLIC: PAYLOAD & OUTPUT    */
    /*******************************/

    /** Builds the normalized VeriFactu domain payload from registry snapshot. */
    Payload(): VerifactuPayload | null {
        const _current = _VerifactuCalc.CurrentChange(this.registry);
        if (!_current){
            return null;
        }

        const _series = _VerifactuCalc.Text(_current.series || this.registry.ticketRef?.series);
        const _invoice = _VerifactuCalc.Text(_current.invoice || this.registry.ticketRef?.invceno);
        const _issuedAtRaw = _VerifactuCalc.Text(_current.date || this.registry.fiscal?.issuedAt);
        const _merchantTaxId = _VerifactuCalc.Text(this.registry.merchant?.taxId);
        const _merchantName = _VerifactuCalc.Text(this.registry.merchant?.name);

        if (!_series || !_invoice || !_issuedAtRaw || !_merchantTaxId || !_merchantName){
            return null;
        }

        const _isDeleted = this.IsCancelled();
        const _operation: VerifactuOperation = (this.IsErrorneous()) ? 'BAJA' : 'ALTA';
        const _reason = _current.reason || null;
        const _issuedAt = this.DateOnly(_issuedAtRaw);
        const _invoiceNumber = _series + '/' + _invoice;
        const _taxes = _isDeleted
            ? this.Amount(0)
            : this.Amount(_current.taxes ?? this.registry.commercial?.totals?.amount ?? 0);
        const _total = _isDeleted
            ? this.Amount(0)
            : this.Amount(_current.total ?? this.registry.commercial?.totals?.total ?? 0);
        const _previous = _VerifactuCalc.PreviousChange(this.registry);
        const _type = this.InvoiceType(_reason, _isDeleted);
        const _recipient = this.Recipient();
        const _rectification = this.Rectification(_reason, _isDeleted, _current, _previous);
        const _replaces = this.ReplacedInvoices(_reason);
        const _rectifies = this.RectifiedInvoices(_reason, _isDeleted);
        const _taxesDetail = this.TaxesDetail(_reason, _isDeleted, _current, _previous, _taxes);
        const _generatedAt = new Date().toISOString();
        const _resolvedChain = this._ResolvedPreviousChain();
        const _description = this.Description(_reason, _isDeleted, _merchantName);

        const _cancelled = (_operation === 'BAJA')
            ? this.CancelledTarget(_current, _previous, _merchantTaxId, _invoiceNumber, _issuedAt)
            : undefined;

        const _software: VerifactuSoftware = {
            developerName: AppConstants.SoftwareDeveloperName,
            developerTaxId: AppConstants.SoftwareDeveloperTaxId,
            appName: AppConstants.Appname,
            appId: AppConstants.SoftwareSystemId,
            appVersion: AppConstants.Version,
            installation: this.installationNumber
        };

        const _payload: VerifactuPayload = {
            operation: _operation,
            reason: _reason,
            issuer: {
                name: _merchantName,
                taxId: _merchantTaxId
            },
            representative: {
                name: _software.developerName,
                taxId: _software.developerTaxId
            },
            invoice: {
                number: _invoiceNumber,
                date: _issuedAt,
                type: _type,
                description: _description,
                taxes: _taxes,
                total: _total,
                taxesDetail: _taxesDetail,
                recipient: _recipient,
                rectification: _rectification,
                replaces: _replaces,
                rectifies: _rectifies
            },
            cancelled: _cancelled,
            previous: (() => {
                const _number = _resolvedChain?.number ?? null;
                const _date = _resolvedChain?.date ?? null;
                if (_number === null || _date === null){
                    return null;
                }

                return { number: _number, date: _date };
            })(),
            previousSignature: _resolvedChain?.signature || null,
            generatedAt: _generatedAt,
            software: _software
        };

        if (_payload.operation !== 'BAJA' && this._BlocksZeroRate(_recipient?.taxId, _recipient?.country)){
            console.error('[VERIFACTU] TipoImpositivo 0 requires a valid Spanish recipient NIF starting with R, G, Q, N or W.');
            return null;
        }

        return _payload;
    }

    /** Calculates the SHA-256 hash source signature for the given payload. */
    async Signature(payload: VerifactuPayload): Promise<string | null> {
        const _source = (payload.operation === 'BAJA')
            ? this.HashSourceBaja(payload)
            : this.HashSourceAlta(payload);
        const _hash = await this.Sha256UpperHex(_source);
        return _hash;
    }

    /** Builds the SOAP XML document for the given payload and signature. */
    Xml(payload: VerifactuPayload, signature: string): string {
        return (payload.operation === 'BAJA')
            ? this.XmlBaja(payload, signature)
            : this.XmlAlta(payload, signature);
    }

    /** Builds the printable VeriFactu QR validation URL. */
    Qr(payload: VerifactuPayload): string {
        const _base = AppConstants.VeriFactuSandbox
            ? 'https://prewww2.aeat.es/wlpl/TIKE-CONT/ValidarQR'
            : 'https://www2.agenciatributaria.gob.es/wlpl/TIKE-CONT/ValidarQR';

        return _base
            + '?nif=' + encodeURIComponent(payload.issuer.taxId)
            + '&numserie=' + encodeURIComponent(payload.invoice.number)
            + '&fecha=' + encodeURIComponent(payload.invoice.date)
            + '&importe=' + encodeURIComponent(payload.invoice.total);
    }

    /*******************************/
    /* PRIVATE: PAYLOAD BUILDERS   */
    /*******************************/

    /** Resolves VeriFactu invoice type code from change reason and cancellation state. */
    private InvoiceType(reason: string | null, isDeleted: boolean): string {
        if (isDeleted){
            return this.Recipient() ? 'R1' : 'R5';
        }
        if (reason === 'I'){
            return 'F3';
        }
        if (reason === 'D'){
            return 'R4';
        }
        if (reason === 'P'){
            return this.Recipient() ? 'R1' : 'R5';
        }
        return 'F2';
    }

    /** Resolves human-readable invoice description from change reason. */
    private Description(reason: string | null, isDeleted: boolean, merchantName: string): string {
        if (isDeleted){
            return 'Rectificacion por cancelacion';
        }
        switch (reason){
            case 'I':
                return 'Emisión de factura';
            case 'D':
                return 'Cambios en destinatario';
            case 'P':
                return 'Cambios en importe';
            default:
                return 'Factura simplificada: ' + merchantName;
        }
    }

    /** Builds recipient data from registry snapshot when present. */
    private Recipient(): VerifactuRecipient | null {
        const _recipient = this.registry.recipient;
        if (!_recipient){
            return null;
        }

        const _name = String(_recipient.name || '').trim();
        const _taxId = String(_recipient.taxId || '').trim();
        if (!_name || !_taxId){
            return null;
        }

        return {
            name: _name,
            taxId: _taxId,
            country: _recipient.country,
        };
    }

    /** Builds rectification metadata for amount or recipient changes. */
    private Rectification(
        reason: string | null,
        isDeleted: boolean,
        current: TicketDataRegistry['changes'][number],
        previous: TicketDataRegistry['changes'][number] | null
    ): VerifactuRectification | null {
        if (isDeleted){
            const _source = previous || current;
            return {
                type: 'S',
                amount: {
                    base: this.Amount(_source.base),
                    taxes: this.Amount(_source.taxes)
                }
            };
        }

        if (reason === 'D'){
            return {
                type: 'S',
                amount: previous
                    ? {
                        base: this.Amount(previous.base),
                        taxes: this.Amount(previous.taxes)
                    }
                    : null
            };
        }
        if (reason === 'P'){
            return {
                type: 'S',
                amount: previous
                    ? {
                        base: this.Amount(previous.base),
                        taxes: this.Amount(previous.taxes)
                    }
                    : null
            };
        }
        return null;
    }

    /** Lists invoices replaced by the current emission change. */
    private ReplacedInvoices(reason: string | null): VerifactuInvoiceRef[] {
        if (reason !== 'I'){
            return [];
        }
        const _changes = this.registry.changes || [];
        return _changes
            .slice(0, -1)
            .map((change) => ({
                number: String(change.series || '') + '/' + String(change.invoice || ''),
                date: this.DateOnly(change.date || '')
            }))
            .filter((entry) => !!entry.number && !!entry.date);
    }

    /** Lists invoices rectified by the current change. */
    private RectifiedInvoices(reason: string | null, isDeleted: boolean): VerifactuInvoiceRef[] {
        if (!reason || (![ 'D', 'P' ].includes(reason) && !isDeleted)){
            return [];
        }
        const _previous = _VerifactuCalc.PreviousChange(this.registry);
        const _source = _previous || _VerifactuCalc.CurrentChange(this.registry);
        if (!_source){
            return [];
        }
        return [{
            number: String(_source.series || '') + '/' + String(_source.invoice || ''),
            date: this.DateOnly(_source.date || '')
        }];
    }

    /** Builds VAT breakdown lines for the current invoice state. */
    private TaxesDetail(
        reason: string | null,
        isDeleted: boolean,
        current: TicketDataRegistry['changes'][number],
        previous: TicketDataRegistry['changes'][number] | null,
        fallbackTaxes: string
    ): VerifactuTaxDetail[] {
        void reason;
        void current;
        void previous;

        if (isDeleted){
            return [{
                rate: 0,
                base: '0.00',
                amount: '0.00'
            }];
        }

        const _taxes = this.registry.commercial?.totals?.taxes || [];
        if (_taxes.length > 0){
            const _sanitized = TaxBreakdown(_taxes);

            return _sanitized.map((tax) => ({
                rate: Number(tax.rate || 0),
                base: this.Amount(tax.base),
                amount: this.Amount(tax.amount)
            }));
        }

        return [{
            rate: 10,
            base: '0.00',
            amount: this.Amount(fallbackTaxes)
        }];
    }

    /** Returns whether the commercial state represents a cancelled ticket. */
    private IsCancelled(): boolean {
        return ['CANCEL', 'DELETE'].includes(this.registry.commercial?.state || '');
    }

    /** Returns whether the commercial state represents an erroneous deletion. */
    private IsErrorneous(): boolean {
        return this.registry.commercial?.state === 'DELETE';
    }

    /** Builds cancellation target metadata for BAJA operations. */
    private CancelledTarget(
        current: TicketDataRegistry['changes'][number],
        previous: TicketDataRegistry['changes'][number] | null,
        merchantTaxId: string,
        currentInvoiceNumber: string,
        currentIssuedAt: string
    ): NonNullable<VerifactuPayload['cancelled']> {
        const _source = previous || current;
        const _series = _VerifactuCalc.Text(_source.series);
        const _invoice = _VerifactuCalc.Text(_source.invoice);
        const _number = (_series && _invoice) ? (_series + '/' + _invoice) : currentInvoiceNumber;
        return {
            taxId: merchantTaxId,
            number: _number,
            date: this.DateOnly(_source.date || currentIssuedAt)
        };
    }

    /*******************************/
    /* PRIVATE: HASH & XML         */
    /*******************************/

    /** Builds hash source string for ALTA registrations. */
    private HashSourceAlta(payload: VerifactuPayload): string {
        return 'IDEmisorFactura=' + payload.issuer.taxId
            + '&NumSerieFactura=' + payload.invoice.number
            + '&FechaExpedicionFactura=' + payload.invoice.date
            + '&TipoFactura=' + payload.invoice.type
            + '&CuotaTotal=' + payload.invoice.taxes
            + '&ImporteTotal=' + payload.invoice.total
            + '&Huella=' + (payload.previousSignature || '')
            + '&FechaHoraHusoGenRegistro=' + payload.generatedAt;
    }

    /** Builds hash source string for BAJA registrations. */
    private HashSourceBaja(payload: VerifactuPayload): string {
        const _cancelled = payload.cancelled;
        return 'IDEmisorFacturaAnulada=' + String(_cancelled?.taxId || '')
            + '&NumSerieFacturaAnulada=' + String(_cancelled?.number || '')
            + '&FechaExpedicionFacturaAnulada=' + String(_cancelled?.date || '')
            + '&Huella=' + (payload.previousSignature || '')
            + '&FechaHoraHusoGenRegistro=' + payload.generatedAt;
    }

    /** Calculates SHA-256 digest and returns uppercase hexadecimal text. */
    private async Sha256UpperHex(input: string): Promise<string | null> {
        try {
            if (!globalThis.crypto?.subtle){
                return null;
            }
            const _buffer = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
            return Array.from(new Uint8Array(_buffer))
                .map((b) => b.toString(16).padStart(2, '0'))
                .join('')
                .toUpperCase();
        }
        catch {
            return null;
        }
    }

    /** Builds SOAP XML for ALTA registrations. */
    private XmlAlta(payload: VerifactuPayload, signature: string): string {
        let _xml = '';

        _xml += '<?xml version="1.0" encoding="UTF-8"?>';
        _xml += '<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/"';
        _xml += ' xmlns:sfLR="https://www2.agenciatributaria.gob.es/static_files/common/internet/dep/aplicaciones/es/aeat/tike/cont/ws/SuministroLR.xsd"';
        _xml += ' xmlns:sf="https://www2.agenciatributaria.gob.es/static_files/common/internet/dep/aplicaciones/es/aeat/tike/cont/ws/SuministroInformacion.xsd">';
        _xml += '<soapenv:Header/>';
        _xml += '<soapenv:Body>';
        _xml += '<sfLR:RegFactuSistemaFacturacion>';
        _xml += '<sfLR:Cabecera>';
        _xml += '<sf:ObligadoEmision>';
        _xml += this.Node('sf:NombreRazon', payload.issuer.name);
        _xml += this.Node('sf:NIF', payload.issuer.taxId);
        _xml += '</sf:ObligadoEmision>';
        _xml += '<sf:Representante>';
        _xml += this.Node('sf:NombreRazon', payload.representative.name);
        _xml += this.Node('sf:NIF', payload.representative.taxId);
        _xml += '</sf:Representante>';
        _xml += '</sfLR:Cabecera>';
        _xml += '<sfLR:RegistroFactura>';
        _xml += '<sf:RegistroAlta>';
        _xml += '<sf:IDVersion>1.0</sf:IDVersion>';
        _xml += '<sf:IDFactura>';
        _xml += this.Node('sf:IDEmisorFactura', payload.issuer.taxId);
        _xml += this.Node('sf:NumSerieFactura', payload.invoice.number);
        _xml += this.Node('sf:FechaExpedicionFactura', payload.invoice.date);
        _xml += '</sf:IDFactura>';
        _xml += this.Node('sf:NombreRazonEmisor', payload.issuer.name);
        _xml += this.Node('sf:TipoFactura', payload.invoice.type);

        if (payload.invoice.rectification?.type){
            _xml += this.Node('sf:TipoRectificativa', payload.invoice.rectification.type);
        }

        if (payload.invoice.replaces.length > 0){
            _xml += '<sf:FacturasSustituidas>';
            for (const _invoice of payload.invoice.replaces){
                _xml += '<sf:IDFacturaSustituida>';
                _xml += this.Node('sf:IDEmisorFactura', payload.issuer.taxId);
                _xml += this.Node('sf:NumSerieFactura', _invoice.number);
                _xml += this.Node('sf:FechaExpedicionFactura', _invoice.date);
                _xml += '</sf:IDFacturaSustituida>';
            }
            _xml += '</sf:FacturasSustituidas>';
        }

        if (payload.invoice.rectifies.length > 0){
            _xml += '<sf:FacturasRectificadas>';
            for (const _invoice of payload.invoice.rectifies){
                _xml += '<sf:IDFacturaRectificada>';
                _xml += this.Node('sf:IDEmisorFactura', payload.issuer.taxId);
                _xml += this.Node('sf:NumSerieFactura', _invoice.number);
                _xml += this.Node('sf:FechaExpedicionFactura', _invoice.date);
                _xml += '</sf:IDFacturaRectificada>';
            }
            _xml += '</sf:FacturasRectificadas>';
        }

        if (payload.invoice.rectification?.amount){
            _xml += '<sf:ImporteRectificacion>';
            _xml += this.Node('sf:BaseRectificada', payload.invoice.rectification.amount.base);
            _xml += this.Node('sf:CuotaRectificada', payload.invoice.rectification.amount.taxes);
            _xml += '</sf:ImporteRectificacion>';
        }

        _xml += this.Node('sf:DescripcionOperacion', payload.invoice.description);

        if (payload.invoice.recipient && ![ 'F2', 'R5' ].includes(payload.invoice.type)){
            _xml += '<sf:Destinatarios>';
            _xml += '<sf:IDDestinatario>';
            _xml += this.Node('sf:NombreRazon', payload.invoice.recipient.name);
            _xml += this.RecipientIdentificationXml(payload.invoice.recipient);
            _xml += '</sf:IDDestinatario>';
            _xml += '</sf:Destinatarios>';
        }

        _xml += '<sf:Desglose>';

        for (const _tax of payload.invoice.taxesDetail){
            _xml += '<sf:DetalleDesglose>';
            _xml += '<sf:ClaveRegimen>01</sf:ClaveRegimen>';
            _xml += '<sf:CalificacionOperacion>S1</sf:CalificacionOperacion>';
            _xml += this.Node('sf:TipoImpositivo', this.Amount(_tax.rate));
            _xml += this.Node('sf:BaseImponibleOimporteNoSujeto', _tax.base);
            _xml += this.Node('sf:CuotaRepercutida', _tax.amount);
            _xml += '</sf:DetalleDesglose>';
        }

        _xml += '</sf:Desglose>';
        _xml += this.Node('sf:CuotaTotal', payload.invoice.taxes);
        _xml += this.Node('sf:ImporteTotal', payload.invoice.total);
        _xml += '<sf:Encadenamiento>';

        if (payload.previous && payload.previousSignature){
            _xml += '<sf:RegistroAnterior>';
            _xml += this.Node('sf:IDEmisorFactura', payload.issuer.taxId);
            _xml += this.Node('sf:NumSerieFactura', payload.previous.number);
            _xml += this.Node('sf:FechaExpedicionFactura', payload.previous.date);
            _xml += this.Node('sf:Huella', payload.previousSignature);
            _xml += '</sf:RegistroAnterior>';
        }
        else {
            _xml += '<sf:PrimerRegistro>S</sf:PrimerRegistro>';
        }

        _xml += '</sf:Encadenamiento>';
        _xml += this.SoftwareXml(payload.software);
        _xml += this.Node('sf:FechaHoraHusoGenRegistro', payload.generatedAt);
        _xml += '<sf:TipoHuella>01</sf:TipoHuella>';
        _xml += this.Node('sf:Huella', signature);
        _xml += '</sf:RegistroAlta>';
        _xml += '</sfLR:RegistroFactura>';
        _xml += '</sfLR:RegFactuSistemaFacturacion>';
        _xml += '</soapenv:Body>';
        _xml += '</soapenv:Envelope>';
        
        return _xml;
    }

    /** Builds SOAP XML for BAJA registrations. */
    private XmlBaja(payload: VerifactuPayload, signature: string): string {
        const _cancelled = payload.cancelled || {
            taxId: payload.issuer.taxId,
            number: payload.invoice.number,
            date: payload.invoice.date
        };

        let _xml = '';
        
        _xml += '<?xml version="1.0" encoding="UTF-8"?>';
        _xml += '<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/"';
        _xml += ' xmlns:sfLR="https://www2.agenciatributaria.gob.es/static_files/common/internet/dep/aplicaciones/es/aeat/tike/cont/ws/SuministroLR.xsd"';
        _xml += ' xmlns:sf="https://www2.agenciatributaria.gob.es/static_files/common/internet/dep/aplicaciones/es/aeat/tike/cont/ws/SuministroInformacion.xsd">';
        _xml += '<soapenv:Header/>';
        _xml += '<soapenv:Body>';
        _xml += '<sfLR:RegFactuSistemaFacturacion>';
        _xml += '<sfLR:Cabecera>';
        _xml += '<sf:ObligadoEmision>';
        _xml += this.Node('sf:NombreRazon', payload.issuer.name);
        _xml += this.Node('sf:NIF', payload.issuer.taxId);
        _xml += '</sf:ObligadoEmision>';
        _xml += '<sf:Representante>';
        _xml += this.Node('sf:NombreRazon', payload.representative.name);
        _xml += this.Node('sf:NIF', payload.representative.taxId);
        _xml += '</sf:Representante>';
        _xml += '</sfLR:Cabecera>';
        _xml += '<sfLR:RegistroFactura>';
        _xml += '<sf:RegistroAnulacion>';
        _xml += '<sf:IDVersion>1.0</sf:IDVersion>';
        _xml += '<sf:IDFactura>';
        _xml += this.Node('sf:IDEmisorFacturaAnulada', _cancelled.taxId);
        _xml += this.Node('sf:NumSerieFacturaAnulada', _cancelled.number);
        _xml += this.Node('sf:FechaExpedicionFacturaAnulada', _cancelled.date);
        _xml += '</sf:IDFactura>';
        _xml += '<sf:Encadenamiento>';

        if (payload.previous && payload.previousSignature){
            _xml += '<sf:RegistroAnterior>';
            _xml += this.Node('sf:IDEmisorFactura', payload.issuer.taxId);
            _xml += this.Node('sf:NumSerieFactura', payload.previous.number);
            _xml += this.Node('sf:FechaExpedicionFactura', payload.previous.date);
            _xml += this.Node('sf:Huella', payload.previousSignature);
            _xml += '</sf:RegistroAnterior>';
        }
        else {
            _xml += '<sf:PrimerRegistro>S</sf:PrimerRegistro>';
        }

        _xml += '</sf:Encadenamiento>';
        _xml += this.SoftwareXml(payload.software);
        _xml += this.Node('sf:FechaHoraHusoGenRegistro', payload.generatedAt);
        _xml += '<sf:TipoHuella>01</sf:TipoHuella>';
        _xml += this.Node('sf:Huella', signature);
        _xml += '</sf:RegistroAnulacion>';
        _xml += '</sfLR:RegistroFactura>';
        _xml += '</sfLR:RegFactuSistemaFacturacion>';
        _xml += '</soapenv:Body>';
        _xml += '</soapenv:Envelope>';

        return _xml;
    }

    /** Builds recipient identification XML using NIF or IDOtro rules. */
    private RecipientIdentificationXml(recipient: VerifactuRecipient): string {
        if (verifactuRecipientUsesNif(recipient)){
            return this.Node('sf:NIF', recipient.taxId);
        }

        const _idOtro = verifactuRecipientIdOtro(recipient);
        if (!_idOtro){
            return '';
        }

        let _xml = '<sf:IDOtro>';
        if (_idOtro.codigoPais){
            _xml += this.Node('sf:CodigoPais', _idOtro.codigoPais);
        }
        _xml += this.Node('sf:IDType', _idOtro.idType);
        _xml += this.Node('sf:ID', _idOtro.id);
        _xml += '</sf:IDOtro>';
        return _xml;
    }

    /** Builds a single XML element with escaped text content. */
    private Node(name: string, value: string): string {
        return '<' + name + '>' + this.Escape(value) + '</' + name + '>';
    }

    /** Builds software metadata XML block required by VeriFactu schemas. */
    private SoftwareXml(software: VerifactuSoftware): string {
        let _xml = '';

        _xml += '<sf:SistemaInformatico>';
        _xml += this.Node('sf:NombreRazon', software.developerName);
        _xml += this.Node('sf:NIF', software.developerTaxId);
        _xml += this.Node('sf:NombreSistemaInformatico', software.appName);
        _xml += this.Node('sf:IdSistemaInformatico', software.appId);
        _xml += this.Node('sf:Version', software.appVersion);
        _xml += this.Node('sf:NumeroInstalacion', software.installation);
        _xml += '<sf:TipoUsoPosibleSoloVerifactu>N</sf:TipoUsoPosibleSoloVerifactu>';
        _xml += '<sf:TipoUsoPosibleMultiOT>S</sf:TipoUsoPosibleMultiOT>';
        _xml += '<sf:IndicadorMultiplesOT>S</sf:IndicadorMultiplesOT>';
        _xml += '</sf:SistemaInformatico>';

        return _xml;
    }

    /** Escapes XML special characters in text nodes. */
    private Escape(value: string): string {
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&apos;');
    }

    /** Formats numeric values as fixed two-decimal strings. */
    private Amount(value: number | string | null | undefined): string {
        const _number = Number(value);
        if (!Number.isFinite(_number)){
            return '0.00';
        }
        return _number.toFixed(2);
    }

    /** Formats an ISO date string as DD-MM-YYYY for VeriFactu XML. */
    private DateOnly(value: string): string {
        const _date = new Date(value);
        if (Number.isNaN(_date.getTime())){
            return '';
        }
        const _dd = String(_date.getDate()).padStart(2, '0');
        const _mm = String(_date.getMonth() + 1).padStart(2, '0');
        const _yyyy = _date.getFullYear();
        return _dd + '-' + _mm + '-' + _yyyy;
    }

}

/*******************************/
/* VERIFACTU INVOICE CALC      */
/*******************************/

class _VerifactuInvoiceCalc {
    /*******************************/
    /* PUBLIC FACTORY              */
    /*******************************/

    /** Validates account invoice prerequisites and returns a calculation context. */
    static Create(
        invoice: AccountInvoice,
        cachedChain: VerifactuPrevious | null,
        installationNumber: string | null
    ): _VerifactuInvoiceCalc | null {
        const _place = invoice.place;
        const _merchantTaxId = _VerifactuInvoiceCalc._Text(_place?.taxid);
        const _merchantName = _VerifactuInvoiceCalc._Text(_place?.business);
        if (!_place || !_merchantTaxId || !_merchantName){
            return null;
        }

        if (!invoice.series || !invoice.invoice){
            return null;
        }

        if (invoice.tickets.length === 0){
            return null;
        }

        return new _VerifactuInvoiceCalc(invoice, cachedChain, String(installationNumber || 'UNKNOWN'));
    }

    /*******************************/
    /* CONSTRUCTOR                 */
    /*******************************/

    /** Private constructor; use Create() to instantiate after validation. */
    private constructor(
        private invoice: AccountInvoice,
        private cachedChain: VerifactuPrevious | null,
        private installationNumber: string
    ){
        // Keep the constructor private: callers must pass through Create() validation.
    }

    /** Economic 0% on any linked ticket may be emitted only for an eligible recipient NIF. */
    private _BlocksZeroRate(taxId: string | null | undefined, country: string | null | undefined): boolean {
        const _zero = this.invoice.tickets.some((_ticket) => {
            const _view = _ticket.View as TicketView | null;
            return _view?.ZeroRateInvoice === true;
        });
        if (!_zero){
            return false;
        }

        return !TaxIdValidators.IsZeroRate(taxId, country);
    }

    /*******************************/
    /* PUBLIC: PAYLOAD             */
    /*******************************/

    /** Builds the normalized VeriFactu payload for a recap invoice. */
    Payload(): VerifactuPayload | null {
        const _place = this.invoice.place;
        const _merchantTaxId = _VerifactuInvoiceCalc._Text(_place?.taxid);
        const _merchantName = _VerifactuInvoiceCalc._Text(_place?.business);
        if (!_merchantTaxId || !_merchantName){
            return null;
        }

        const _issuedAtRaw = this._IssuedAt();
        const _invoiceNumber = String(this.invoice.series || '') + '/' + String(this.invoice.invoice || '');
        const _taxesDetail = this._TaxesDetail();
        const _taxes = this._Amount(_taxesDetail.reduce((sum, tax) => sum + Number(tax.amount), 0));
        const _total = this._Amount(_taxesDetail.reduce((sum, tax) => sum + Number(tax.base) + Number(tax.amount), 0));
        const _generatedAt = new Date().toISOString();
        const _resolvedChain = this._ResolvedPreviousChain();
        const _software: VerifactuSoftware = {
            developerName: AppConstants.SoftwareDeveloperName,
            developerTaxId: AppConstants.SoftwareDeveloperTaxId,
            appName: AppConstants.Appname,
            appId: AppConstants.SoftwareSystemId,
            appVersion: AppConstants.Version,
            installation: this.installationNumber,
        };

        const _recipient = this._Recipient();
        const _payload: VerifactuPayload = {
            operation: 'ALTA',
            reason: 'A',
            issuer: {
                name: _merchantName,
                taxId: _merchantTaxId,
            },
            representative: {
                name: _software.developerName,
                taxId: _software.developerTaxId,
            },
            invoice: {
                number: _invoiceNumber,
                date: this._DateOnly(_issuedAtRaw),
                type: 'F3',
                description: 'Factura recapitulativa',
                taxes: _taxes,
                total: _total,
                taxesDetail: _taxesDetail,
                recipient: _recipient,
                rectification: null,
                replaces: this._ReplacedInvoices(),
                rectifies: [],
            },
            previous: (() => {
                const _number = _resolvedChain?.number ?? null;
                const _date = _resolvedChain?.date ?? null;
                if (_number === null || _date === null){
                    return null;
                }

                return { number: _number, date: _date };
            })(),
            previousSignature: _resolvedChain?.signature || null,
            generatedAt: _generatedAt,
            software: _software,
        };

        if (this._BlocksZeroRate(_recipient?.taxId, _recipient?.country)){
            console.error('[VERIFACTU] TipoImpositivo 0 requires a valid Spanish recipient NIF starting with R, G, Q, N or W.');
            return null;
        }

        return _payload;
    }

    /*******************************/
    /* PRIVATE: PAYLOAD BUILDERS   */
    /*******************************/

    /** Builds recipient data from linked account client when present. */
    private _Recipient(): VerifactuRecipient | null {
        const _client = this.invoice.account?.client ?? null;
        if (!_client){
            return null;
        }

        const _name = String(_client.name || '').trim();
        const _taxId = String(_client.taxid || '').trim();
        if (!_name || !_taxId){
            return null;
        }

        return {
            name: _name,
            taxId: _taxId,
            country: _client.country ?? null,
        };
    }

    /** Lists simplified tickets replaced by the recap invoice. */
    private _ReplacedInvoices(): VerifactuInvoiceRef[] {
        return this.invoice.tickets
            .map((_ticket) => ({
                number: String(_ticket.series || '') + '/' + String(_ticket.invceno || ''),
                date: this._DateOnly(this._TicketIssuedAt(_ticket)),
            }))
            .filter((entry) => !!entry.number && !!entry.date);
    }

    /** Builds VAT breakdown lines from invoice tax split. */
    private _TaxesDetail(): VerifactuTaxDetail[] {
        const _split = this.invoice.SplitTaxes.price.tax.split;
        if (_split.length > 0){
            const _sanitized = TaxBreakdown(
                _split.map((_tax) => ({
                    rate: Number(_tax.rate || 0),
                    base: Number(_tax.base || 0),
                    amount: Number(_tax.taxes || 0),
                    total: Number(_tax.total || 0),
                }))
            );

            return _sanitized.map((_tax) => ({
                rate: Number(_tax.rate || 0),
                base: this._Amount(_tax.base),
                amount: this._Amount(_tax.amount),
            }));
        }

        return [{
            rate: 10,
            base: '0.00',
            amount: this._Amount(this.invoice.total),
        }];
    }

    /** Builds previous invoice chain reference from cached device data. */
    private _ResolvedPreviousChain(): VerifactuPrevious | null {
        const _chain = this.cachedChain;
        if (!_chain?.signature){
            return null;
        }

        const _currentNumber = String(this.invoice.series || '') + '/' + String(this.invoice.invoice || '');
        if (_chain.number === _currentNumber){
            return null;
        }

        return verifactuResolvedPreviousChain({
            number: _chain.number,
            date: _chain.date ? this._DateOnly(_chain.date) : null,
            signature: _chain.signature,
        });
    }

    /** Resolves recap invoice issue timestamp as ISO string. */
    private _IssuedAt(): string {
        const _created = this.invoice.created;
        if (_created instanceof Date){
            return _created.toISOString();
        }

        if (_created){
            return String(_created);
        }

        return new Date().toISOString();
    }

    /** Resolves ticket issue timestamp, falling back to recap invoice date. */
    private _TicketIssuedAt(ticket: Ticket): string {
        const _created = ticket.created;
        if (_created instanceof Date){
            return _created.toISOString();
        }

        if (_created){
            return String(_created);
        }

        return this._IssuedAt();
    }

    /*******************************/
    /* PRIVATE: FORMAT HELPERS     */
    /*******************************/

    /** Trims and normalizes optional text values. */
    private static _Text(value: string | null | undefined): string | null {
        const _text = String(value || '').trim();
        return _text ? _text : null;
    }

    /** Formats numeric values as fixed two-decimal strings. */
    private _Amount(value: number | string | null | undefined): string {
        const _number = Number(value);
        if (!Number.isFinite(_number)){
            return '0.00';
        }
        return _number.toFixed(2);
    }

    /** Formats an ISO date string as DD-MM-YYYY for VeriFactu payloads. */
    private _DateOnly(value: string): string {
        const _date = new Date(value);
        if (Number.isNaN(_date.getTime())){
            return '';
        }
        const _dd = String(_date.getDate()).padStart(2, '0');
        const _mm = String(_date.getMonth() + 1).padStart(2, '0');
        const _yyyy = _date.getFullYear();
        return _dd + '-' + _mm + '-' + _yyyy;
    }
}
